import { esCelularOTablet } from '../utils/dispositivo';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Tip } from './ui/tip';
import { XMarkIcon, BoltIcon, CameraIcon, PencilIcon } from '@heroicons/react/24/outline';
import { cargarOrt, crearSesion, backendActivo } from '../lib/ortRuntime';

/**
 * Escáner de patentes EN VIVO, estilo tótem: apuntás la cámara y la patente
 * aparece sola. Corre 100% en el navegador con dos modelos ONNX locales
 * (detector YOLO de chapas + OCR entrenado con patentes argentinas, de
 * fast-alpr / cnn-ocr-lp, MIT). Nada sale del teléfono y no cuesta nada por uso.
 */

// ── Formatos de patente argentinos ──────────────────────────────────────────
const FORMATOS_AR = [
  /^[A-Z]{2}\d{3}[A-Z]{2}$/, // Mercosur autos: AB123CD
  /^[A-Z]{3}\d{3}$/, // viejo autos: ABC123
  /^[A-Z]\d{3}[A-Z]{3}$/, // Mercosur motos: A123BCD
  /^\d{3}[A-Z]{3}$/ // viejo motos: 123ABC
];
export const esPatenteValida = (p: string) => FORMATOS_AR.some((rx) => rx.test(p));

// ── Sugerencias desde la base (chapas sucias/rotas) ─────────────────────────
// Solo se sugiere una patente conocida si TODO lo que difiere de la lectura
// son caracteres que se confunden VISUALMENTE (3↔8, 0↔O, 2↔Z...). Un 3 contra
// un 4 no se parecen en nada → esa patente no se sugiere aunque exista.
const CONFUNDIBLES: Record<string, string> = {
  '0': 'ODQ', O: '0DQ', D: '0OB', Q: '0O',
  '1': 'IL', I: '1L', L: '1I',
  '2': 'Z', Z: '2',
  '3': '8', '8': 'B3', B: '8D',
  '4': 'A', A: '4',
  '5': 'S', S: '5',
  '6': 'G', G: '6',
  '7': 'T', T: '7',
  E: 'F', F: 'E',
  M: 'N', N: 'M',
  V: 'W', W: 'V',
  K: 'X', X: 'K'
};
const seConfunden = (a: string, b: string) =>
  (CONFUNDIBLES[a] || '').includes(b) || (CONFUNDIBLES[b] || '').includes(a);

export interface PatenteConocida { plate: string; label: string }

/** Patentes de la base a 1-2 caracteres CONFUNDIBLES de la lectura, mejor rankeada primero */
export function buscarPatentesParecidas(
  lectura: string,
  confs: number[],
  conocidas: PatenteConocida[]
): PatenteConocida[] {
  if (lectura.length < 6) return [];
  const out: (PatenteConocida & { diffs: number; dudas: number })[] = [];
  for (const k of conocidas) {
    if (k.plate.length !== lectura.length || k.plate === lectura) continue;
    let diffs = 0;
    let dudas = 0;
    let ok = true;
    for (let i = 0; i < lectura.length; i++) {
      if (lectura[i] === k.plate[i]) continue;
      diffs++;
      if (diffs > 2 || !seConfunden(lectura[i], k.plate[i])) { ok = false; break; }
      dudas += 1 - (confs[i] ?? 0.5); // difiere justo donde el OCR dudo → mejor candidata
    }
    if (ok && diffs > 0) out.push({ ...k, diffs, dudas: dudas / diffs });
  }
  return out
    .sort((a, b) => a.diffs - b.diffs || b.dudas - a.dudas)
    .slice(0, 3)
    .map(({ plate, label }) => ({ plate, label }));
}

// Indice liviano de patentes conocidas (se baja una vez por sesion)
let indicePatentesPromise: Promise<PatenteConocida[]> | null = null;
function cargarIndicePatentes(): Promise<PatenteConocida[]> {
  if (!indicePatentesPromise) {
    indicePatentesPromise = axios
      .get('/api/vehicles/plates-index')
      .then((r) =>
        (r.data?.plates || [])
          .map((p: any) => ({
            plate: String(p.plate || '').toUpperCase(),
            label: [[p.make, p.model].filter(Boolean).join(' '), p.clientName].filter(Boolean).join(' · ')
          }))
          .filter((p: PatenteConocida) => p.plate.length >= 6)
      )
      .catch(() => {
        indicePatentesPromise = null; // reintentar en la proxima apertura
        return [];
      });
  }
  return indicePatentesPromise;
}

// ── Modelos (se cargan una sola vez y quedan cacheados) ─────────────────────
const ALFABETO = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_';
const OCR_W = 140;
const OCR_H = 70;
const DET_SIZE = 384;

let ortModulo: any = null;
let sesionesPromise: Promise<{ det: any; ocr: any }> | null = null;

async function cargarSesiones() {
  if (!sesionesPromise) {
    sesionesPromise = (async () => {
      const ort = await cargarOrt();
      ortModulo = ort;
      // WebGPU si el equipo puede, wasm de respaldo (ver lib/ortRuntime)
      const [det, ocr] = await Promise.all([
        crearSesion(ort, '/models/alpr/detector.onnx'),
        crearSesion(ort, '/models/alpr/ocr-arg.onnx')
      ]);
      console.info(`[PlateScanner] modelos listos (backend: ${backendActivo})`);
      return { det, ocr };
    })().catch((e) => {
      sesionesPromise = null; // permitir reintento si fallo la descarga
      throw e;
    });
  }
  return sesionesPromise;
}

// ── Preprocesado del recorte para el OCR ────────────────────────────────────
// Estirado de contraste (percentil 2-98): levanta chapas sucias, con sombra o
// a contraluz sin reventar el ruido
function estirarContraste(src: Uint8Array): Uint8Array {
  const hist = new Uint32Array(256);
  for (let i = 0; i < src.length; i++) hist[src[i]]++;
  const corte = src.length * 0.02;
  let lo = 0;
  let hi = 255;
  for (let v = 0, acc = 0; v < 256; v++) { acc += hist[v]; if (acc >= corte) { lo = v; break; } }
  for (let v = 255, acc = 0; v >= 0; v--) { acc += hist[v]; if (acc >= corte) { hi = v; break; } }
  const out = new Uint8Array(src.length);
  if (hi <= lo + 20) { out.set(src); return out; }
  const esc = 255 / (hi - lo);
  for (let i = 0; i < src.length; i++) {
    const g = (src[i] - lo) * esc;
    out[i] = g < 0 ? 0 : g > 255 ? 255 : g;
  }
  return out;
}

// CLAHE (ecualización adaptativa con límite de contraste): cada zona del
// recorte se ecualiza por su cuenta, así una mitad al sol y la otra en sombra
// quedan ambas legibles. Tiles 4x2 sobre 140x70, clip 2.0, bilineal entre tiles.
function clahe(src: Uint8Array, w = OCR_W, h = OCR_H, tilesX = 4, tilesY = 2, clip = 2.0): Uint8Array {
  const tw = Math.ceil(w / tilesX);
  const th = Math.ceil(h / tilesY);
  const luts: Uint8Array[] = [];
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      const x0 = tx * tw;
      const y0 = ty * th;
      const x1 = Math.min(w, x0 + tw);
      const y1 = Math.min(h, y0 + th);
      const hist = new Uint32Array(256);
      let n = 0;
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) { hist[src[y * w + x]]++; n++; }
      const limite = Math.max(1, Math.floor((clip * n) / 256));
      let exceso = 0;
      for (let i = 0; i < 256; i++) if (hist[i] > limite) { exceso += hist[i] - limite; hist[i] = limite; }
      const extra = Math.floor(exceso / 256);
      const lut = new Uint8Array(256);
      let acc = 0;
      for (let i = 0; i < 256; i++) { acc += hist[i] + extra; lut[i] = Math.min(255, Math.round((acc * 255) / Math.max(1, n))); }
      luts.push(lut);
    }
  }
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const fy = (y + 0.5) / th - 0.5;
    const ty0 = Math.max(0, Math.min(tilesY - 1, Math.floor(fy)));
    const ty1 = Math.min(tilesY - 1, ty0 + 1);
    const wy = Math.max(0, Math.min(1, fy - ty0));
    for (let x = 0; x < w; x++) {
      const fx = (x + 0.5) / tw - 0.5;
      const tx0 = Math.max(0, Math.min(tilesX - 1, Math.floor(fx)));
      const tx1 = Math.min(tilesX - 1, tx0 + 1);
      const wx = Math.max(0, Math.min(1, fx - tx0));
      const v = src[y * w + x];
      const a = luts[ty0 * tilesX + tx0][v];
      const b = luts[ty0 * tilesX + tx1][v];
      const c = luts[ty1 * tilesX + tx0][v];
      const d = luts[ty1 * tilesX + tx1][v];
      out[y * w + x] = Math.round((a * (1 - wx) + b * wx) * (1 - wy) + (c * (1 - wx) + d * wx) * wy);
    }
  }
  return out;
}

/** Precarga modelos + runtime + indice de patentes para que abrir sea instantaneo */
export function precalentarEscanerPatentes() {
  cargarSesiones().catch(() => { /* se reintenta al abrir */ });
  cargarIndicePatentes();
}

interface Props {
  open: boolean;
  onClose: () => void;
  onPlate: (plate: string) => void;
}

const PlateScanner: React.FC<Props> = ({ open, onClose, onPlate }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const vivoRef = useRef(false);
  const [estado, setEstado] = useState<'cargando' | 'buscando' | 'detectada' | 'error'>('cargando');
  const [mensajeError, setMensajeError] = useState('');
  const [lectura, setLectura] = useState('');
  // Lo que el OCR va leyendo aunque todavia no valide (feedback en vivo)
  const [cruda, setCruda] = useState('');
  const [sugerencias, setSugerencias] = useState<PatenteConocida[]>([]);
  const indiceRef = useRef<PatenteConocida[]>([]);
  // Carga manual: por si no detecta nada o detecta cualquier cosa
  const [manualAbierto, setManualAbierto] = useState(false);
  const manualAbiertoRef = useRef(false);
  const [manualValor, setManualValor] = useState('');
  // Dataset para reentrenar (Svetovid): ultimo recorte que vio el OCR y la
  // lectura cruda; al confirmar/corregir se suben con la etiqueta final
  const ultimoCropRef = useRef('');
  const ultimaCrudaRef = useRef('');
  const detectadaRef = useRef(false);
  // El MEJOR recorte de la sesion (no el ultimo): el ultimo cuadro antes de
  // cerrar suele ser un falso positivo del detector (un faro, un cartel)
  const mejorFallidoRef = useRef<{ crop: string; cruda: string; puntaje: number }>({ crop: '', cruda: '', puntaje: -1 });
  const [cuadro, setCuadro] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [conLinterna, setConLinterna] = useState(false);
  const [linternaPrendida, setLinternaPrendida] = useState(false);
  const ultimaLecturaRef = useRef<{ plate: string; veces: number }>({ plate: '', veces: 0 });
  // El telefono puede estar girado (apaisado, o con la rotacion bloqueada):
  // si no aparece nada se prueba el cuadro girado 90/180/270 hasta enganchar
  const rotacionRef = useRef(0);
  const sinDeteccionRef = useRef(0);
  const lecturasInvalidasRef = useRef(0);
  // Zoom para chapas lejanas: nativo de la camara si el equipo lo expone,
  // si no digital (recorte del centro del video, mismo efecto)
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const zoomDigitalRef = useRef(true);
  const [zoomDigital, setZoomDigital] = useState(true);
  // Niveles segun lo que declare la camara (un iPhone Pro llega mucho mas
  // alla de 4x); sin zoom nativo quedan los digitales 1/2/4
  const [nivelesZoom, setNivelesZoom] = useState<number[]>([1, 2, 4]);

  const aplicarZoom = useCallback(async (nivel: number) => {
    // Nativo primero (mejor calidad: lente/sensor real); si el equipo no lo
    // soporta, digital (recorte del centro). Confirmado por Rodrigo 27/08:
    // en su iPhone el nativo aplica bien y los digitos se ven nitidos.
    setZoom(nivel);
    zoomRef.current = nivel;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const caps = (track as any)?.getCapabilities?.();
    if (track && caps?.zoom && nivel >= (caps.zoom.min || 1) && nivel <= caps.zoom.max) {
      try {
        await (track as any).applyConstraints({ advanced: [{ zoom: nivel }] });
        zoomDigitalRef.current = false;
        setZoomDigital(false);
        return;
      } catch { /* cae a digital */ }
    }
    zoomDigitalRef.current = true;
    setZoomDigital(true);
  }, []);

  const apagarTodo = useCallback(() => {
    vivoRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Subir la muestra etiquetada al dataset (fire-and-forget: si falla, nada)
  const guardarMuestra = useCallback((label: string, fuente: string) => {
    // 'auto' = el cuadro que acaba de confirmar; para lo demas (tentativa,
    // sugerencia, manual) el mejor recorte de la sesion, no el ultimo
    const image = fuente === 'auto'
      ? ultimoCropRef.current
      : (mejorFallidoRef.current.crop || ultimoCropRef.current);
    if (!image || !label) return;
    axios.post('/api/vision/sample', {
      kind: 'patente',
      label,
      lectura: ultimaCrudaRef.current,
      fuente,
      image
    }).catch(() => { /* dataset best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intento FALLIDO: se cerro el escaner con una chapa a la vista pero sin
  // lectura confirmada. Se guarda sin etiqueta (lectura tentativa como pista)
  // para etiquetarla despues en tanda: son justo los casos que el modelo
  // no resuelve hoy, la muestra mas valiosa para reentrenar.
  const guardarFallido = useCallback(() => {
    const mejor = mejorFallidoRef.current;
    if (detectadaRef.current || !mejor.crop || mejor.puntaje < 0) return;
    axios.post('/api/vision/sample', {
      kind: 'patente',
      label: '',
      lectura: mejor.cruda,
      fuente: 'fallido',
      image: mejor.crop
    }).catch(() => { /* dataset best-effort */ });
    mejorFallidoRef.current = { crop: '', cruda: '', puntaje: -1 };
  }, []);

  const cerrar = useCallback(() => {
    guardarFallido();
    apagarTodo();
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardarFallido, apagarTodo]);

  // Aceptar a mano una patente (tentativa, sugerida de la base, o tipeada)
  const usarPatente = useCallback((p: string, fuente: string) => {
    if (!p) return;
    detectadaRef.current = true;
    guardarMuestra(p, fuente);
    setLectura(p);
    setEstado('detectada');
    vivoRef.current = false;
    setTimeout(() => {
      onPlate(p);
      onClose();
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La lectura tentativa ("¿AB123CD?") ya paso el formato argentino: solo le
  // falto el segundo cuadro de confirmacion, un toque alcanza
  const confirmarManual = useCallback(() => usarPatente(lectura, 'tentativa'), [lectura, usarPatente]);

  // Corregir: el editor abre con lo que el escaner leyo (tentativa o cruda),
  // asi solo se cambia el caracter equivocado en vez de tipear todo
  const abrirManual = useCallback(() => {
    setManualValor((lectura || cruda || '').replace(/[^A-Z0-9]/g, '').slice(0, 7));
    setManualAbierto(true);
    manualAbiertoRef.current = true;
  }, [lectura, cruda]);

  const toggleLinterna = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await (track as any).applyConstraints({ advanced: [{ torch: !linternaPrendida }] });
      setLinternaPrendida(!linternaPrendida);
    } catch { /* algunos equipos no dejan */ }
  }, [linternaPrendida]);

  useEffect(() => {
    if (!open) { apagarTodo(); return; }
    let cancelado = false;

    (async () => {
      setEstado('cargando');
      setLectura('');
      setCruda('');
      setSugerencias([]);
      setManualAbierto(false);
      manualAbiertoRef.current = false;
      setManualValor('');
      ultimoCropRef.current = '';
      ultimaCrudaRef.current = '';
      detectadaRef.current = false;
      mejorFallidoRef.current = { crop: '', cruda: '', puntaje: -1 };
      cargarIndicePatentes().then((l) => { indiceRef.current = l; });
      setCuadro(null);
      ultimaLecturaRef.current = { plate: '', veces: 0 };
      rotacionRef.current = 0;
      sinDeteccionRef.current = 0;
      lecturasInvalidasRef.current = 0;
      setZoom(1);
      zoomRef.current = 1;
      zoomDigitalRef.current = true;
      setZoomDigital(true);
      try {
        const [{ det, ocr }, stream] = await Promise.all([
          cargarSesiones(),
          navigator.mediaDevices.getUserMedia({
            // 1080p: los recortes del OCR salen mas nitidos (clave para chapas
            // lejos o con zoom); si el equipo no llega, el navegador da menos
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false
          })
        ]);
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        const caps = (track as any).getCapabilities?.();
        setConLinterna(Boolean(caps?.torch));
        // Botones de zoom segun el rango REAL de la camara
        if (caps?.zoom && Number.isFinite(caps.zoom.max) && caps.zoom.max > 1) {
          const niveles = [1, 2, 4, 8, 16, 25]
            .filter((n) => n >= (caps.zoom.min || 1) && n <= caps.zoom.max)
            .slice(0, 5);
          if (niveles.length >= 2) setNivelesZoom(niveles);
        } else {
          setNivelesZoom([1, 2, 4]);
        }
        const video = videoRef.current!;
        video.srcObject = stream;
        await video.play();
        setEstado('buscando');
        vivoRef.current = true;

        const ort = ortModulo;
        const canvasDet = document.createElement('canvas');
        canvasDet.width = DET_SIZE;
        canvasDet.height = DET_SIZE;
        const ctxDet = canvasDet.getContext('2d', { willReadFrequently: true })!;
        const canvasOcr = document.createElement('canvas');
        canvasOcr.width = OCR_W;
        canvasOcr.height = OCR_H;
        const ctxOcr = canvasOcr.getContext('2d', { willReadFrequently: true })!;
        // El recorte baja del frame nativo (1080p) a 140x70 de un saque:
        // remuestreo de calidad alta para no perder los trazos finos
        ctxOcr.imageSmoothingEnabled = true;
        ctxOcr.imageSmoothingQuality = 'high';
        const detInput = new Float32Array(3 * DET_SIZE * DET_SIZE);
        // Voto por caracter: las probabilidades del OCR se acumulan entre
        // cuadros con decaimiento. Una chapa sucia da lecturas parcialmente
        // distintas cuadro a cuadro; el voto converge aunque ninguno sea perfecto.
        let votos: Float32Array | null = null;
        // Cuadro girado a resolucion completa (solo se usa con rotacion != 0)
        const canvasFrame = document.createElement('canvas');
        const ctxFrame = canvasFrame.getContext('2d', { willReadFrequently: true })!;

        // Un cuadro (x,y,w,h normalizado) leido sobre el frame girado, vuelto
        // a las coordenadas de lo que se ve en pantalla (el video sin girar)
        const mapearCuadro = (r: { x: number; y: number; w: number; h: number }) => {
          const rot = rotacionRef.current;
          if (rot === 0) return r;
          const pts = [[r.x, r.y], [r.x + r.w, r.y + r.h]].map(([x, y]) => {
            if (rot === 90) return [y, 1 - x];
            if (rot === 180) return [1 - x, 1 - y];
            return [1 - y, x]; // 270
          });
          const xs = [pts[0][0], pts[1][0]];
          const ys = [pts[0][1], pts[1][1]];
          return {
            x: Math.min(xs[0], xs[1]),
            y: Math.min(ys[0], ys[1]),
            w: Math.abs(xs[0] - xs[1]),
            h: Math.abs(ys[0] - ys[1])
          };
        };

        // Secuencia que vuelve a 0 entre giros: el caso comun (telefono
        // derecho) se prueba el doble de seguido que las otras orientaciones
        const SECUENCIA_ROT = [0, 90, 0, 180, 0, 270];
        let rotIdx = 0;
        const rotarBusqueda = () => {
          rotIdx = (rotIdx + 1) % SECUENCIA_ROT.length;
          rotacionRef.current = SECUENCIA_ROT[rotIdx];
          sinDeteccionRef.current = 0;
          lecturasInvalidasRef.current = 0;
          votos = null;
        };

        const ciclo = async () => {
          if (!vivoRef.current) return;
          try {
            const vw = video.videoWidth;
            const vh = video.videoHeight;
            if (!vw || !vh) { setTimeout(ciclo, 150); return; }

            // Fuente del cuadro: el video tal cual, o girado si el telefono
            // esta en otra orientacion
            const rot = rotacionRef.current;
            let fuente: CanvasImageSource = video;
            let fw = vw;
            let fh = vh;
            if (rot !== 0) {
              fw = rot === 180 ? vw : vh;
              fh = rot === 180 ? vh : vw;
              if (canvasFrame.width !== fw || canvasFrame.height !== fh) {
                canvasFrame.width = fw;
                canvasFrame.height = fh;
              }
              ctxFrame.save();
              ctxFrame.translate(fw / 2, fh / 2);
              ctxFrame.rotate((rot * Math.PI) / 180);
              ctxFrame.drawImage(video, -vw / 2, -vh / 2);
              ctxFrame.restore();
              fuente = canvasFrame;
            }

            // Zoom digital: se procesa (y se muestra) solo el centro del cuadro
            const z = zoomDigitalRef.current ? Math.max(1, zoomRef.current) : 1;
            const cw = fw / z;
            const ch = fh / z;
            const cx = (fw - cw) / 2;
            const cy = (fh - ch) / 2;

            // ── Detección: letterbox 384x384 con gris 114 ──
            const r = Math.min(DET_SIZE / ch, DET_SIZE / cw);
            const nw = Math.round(cw * r);
            const nh = Math.round(ch * r);
            const dw = (DET_SIZE - nw) / 2;
            const dh = (DET_SIZE - nh) / 2;
            ctxDet.fillStyle = 'rgb(114,114,114)';
            ctxDet.fillRect(0, 0, DET_SIZE, DET_SIZE);
            ctxDet.drawImage(fuente, cx, cy, cw, ch, dw, dh, nw, nh);
            const px = ctxDet.getImageData(0, 0, DET_SIZE, DET_SIZE).data;
            const plano = DET_SIZE * DET_SIZE;
            for (let i = 0; i < plano; i++) {
              detInput[i] = px[i * 4] / 255;
              detInput[plano + i] = px[i * 4 + 1] / 255;
              detInput[2 * plano + i] = px[i * 4 + 2] / 255;
            }
            const tensorDet = new ort.Tensor('float32', detInput, [1, 3, DET_SIZE, DET_SIZE]);
            const salidaDet = await det.run({ [det.inputNames[0]]: tensorDet });
            const filas: Float32Array = salidaDet[det.outputNames[0]].data;

            // salida end2end: filas de 7 = [batch, x1, y1, x2, y2, clase, score]
            let mejor: number[] | null = null;
            for (let i = 0; i + 6 < filas.length; i += 7) {
              const score = filas[i + 6];
              if (score > 0.35 && (!mejor || score > mejor[6])) {
                mejor = Array.from(filas.slice(i, i + 7));
              }
            }

            if (!mejor) {
              setCuadro(null);
              ultimaLecturaRef.current = { plate: '', veces: 0 };
              votos = null; // la chapa salio del cuadro: no arrastrar votos viejos
              // Nada a la vista: puede ser que el telefono este girado
              if (++sinDeteccionRef.current >= 2) rotarBusqueda();
              if (vivoRef.current) setTimeout(ciclo, 220);
              return;
            }
            sinDeteccionRef.current = 0;

            // volver a coordenadas del recorte visible (y de ahi al video)
            const bx1 = Math.max(0, (mejor[1] - dw) / r);
            const by1 = Math.max(0, (mejor[2] - dh) / r);
            const bx2 = Math.min(cw, (mejor[3] - dw) / r);
            const by2 = Math.min(ch, (mejor[4] - dh) / r);
            // el overlay se dibuja sobre lo VISIBLE (que con zoom digital es el recorte)
            setCuadro(mapearCuadro({ x: bx1 / cw, y: by1 / ch, w: (bx2 - bx1) / cw, h: (by2 - by1) / ch }));

            // ── OCR: recorte con margen, gris 140x70 (coordenadas absolutas del cuadro) ──
            const margen = (bx2 - bx1) * 0.04;
            const sx = Math.max(0, cx + bx1 - margen);
            const sy = Math.max(0, cy + by1 - margen);
            const sw = Math.min(fw - sx, bx2 - bx1 + margen * 2);
            const sh = Math.min(fh - sy, by2 - by1 + margen * 2);
            ctxOcr.drawImage(fuente, sx, sy, sw, sh, 0, 0, OCR_W, OCR_H);
            // Guardar el recorte para el dataset (es chico: ~4 KB)
            try { ultimoCropRef.current = canvasOcr.toDataURL('image/jpeg', 0.9); } catch { /* sin dataset */ }
            const pxOcr = ctxOcr.getImageData(0, 0, OCR_W, OCR_H).data;
            const gris = new Uint8Array(OCR_W * OCR_H);
            for (let i = 0; i < gris.length; i++) {
              gris[i] = Math.round(0.299 * pxOcr[i * 4] + 0.587 * pxOcr[i * 4 + 1] + 0.114 * pxOcr[i * 4 + 2]);
            }
            // Ensamble de preprocesados: el OCR lee el recorte estirado Y el
            // ecualizado (CLAHE) y se promedian las probabilidades. Ninguno
            // gana siempre (una chapa limpia prefiere el estirado, una sucia o
            // a media sombra el CLAHE): sumando los dos, el que acierta manda.
            const estirado = estirarContraste(gris);
            const ecualizado = clahe(estirado);
            const leerOcr = async (img: Uint8Array): Promise<Float32Array> => {
              const t = new ort.Tensor('uint8', img, [1, OCR_H, OCR_W, 1]);
              const o = await ocr.run({ [ocr.inputNames[0]]: t });
              return o[ocr.outputNames[0]].data as Float32Array;
            };
            const p1 = await leerOcr(estirado);
            const p2 = await leerOcr(ecualizado);
            const probs = new Float32Array(p1.length);
            for (let i = 0; i < probs.length; i++) probs[i] = (p1[i] + p2[i]) / 2;
            // Acumular con decaimiento (0.65): lo viejo pesa cada vez menos
            if (!votos || votos.length !== probs.length) votos = new Float32Array(probs.length);
            for (let i = 0; i < probs.length; i++) votos[i] = votos[i] * 0.65 + probs[i];
            // Lectura de CONSENSO sobre los votos, con confianza por posicion
            const slots = probs.length / ALFABETO.length;
            let placa = '';
            let peorConf = 1;
            const confs: number[] = [];
            for (let s = 0; s < slots; s++) {
              let mejorIdx = 0;
              let mejorVoto = -1;
              let sumaSlot = 0;
              for (let a = 0; a < ALFABETO.length; a++) {
                const v = votos[s * ALFABETO.length + a];
                sumaSlot += v;
                if (v > mejorVoto) { mejorVoto = v; mejorIdx = a; }
              }
              const ch = ALFABETO[mejorIdx];
              if (ch !== '_') {
                const conf = sumaSlot > 0 ? mejorVoto / sumaSlot : 0;
                placa += ch;
                confs.push(conf);
                if (conf < peorConf) peorConf = conf;
              }
            }
            ultimaCrudaRef.current = placa;
            setCruda(placa);
            // Candidato a "mejor recorte" para el dataset de fallidos: pesa
            // mas tener formato de patente valido que la confianza suelta
            if (ultimoCropRef.current) {
              const puntaje = (esPatenteValida(placa) ? 2 : 0) + (placa.length === 7 ? 0.5 : 0) + peorConf + mejor[6] * 0.5;
              if (puntaje > mejorFallidoRef.current.puntaje) {
                mejorFallidoRef.current = { crop: ultimoCropRef.current, cruda: placa, puntaje };
              }
            }

            // Sugerencias desde la base: patentes conocidas que difieren solo
            // en caracteres confundibles (nunca reemplazan, solo se ofrecen)
            if (placa.length >= 6 && indiceRef.current.length) {
              const exacta = indiceRef.current.some((k) => k.plate === placa);
              setSugerencias(exacta ? [] : buscarPatentesParecidas(placa, confs, indiceRef.current));
            }

            // 0.40: la confianza ahora es la porcion de VOTOS acumulados del
            // ganador (mas exigente que la prob de un solo cuadro que era 0.45)
            if (esPatenteValida(placa) && peorConf > 0.4) {
              lecturasInvalidasRef.current = 0;
              const ult = ultimaLecturaRef.current;
              const veces = ult.plate === placa ? ult.veces + 1 : 1;
              ultimaLecturaRef.current = { plate: placa, veces };
              setLectura(placa);
              // dos lecturas iguales seguidas = confirmada (evita falsos por 1 frame)
              // (salvo que el operario este tipeando a mano: no cerrarle el teclado)
              if (veces >= 2 && !manualAbiertoRef.current) {
                detectadaRef.current = true;
                guardarMuestra(placa, 'auto');
                setEstado('detectada');
                vivoRef.current = false;
                setTimeout(() => {
                  onPlate(placa);
                  onClose();
                }, 700);
                return;
              }
            } else if (++lecturasInvalidasRef.current >= 24) {
              // Chapa a la vista pero ilegible MUCHO tiempo: quiza esta patas
              // arriba (180). Umbral alto: girar el barrido con la chapa
              // enganchada corta lecturas que estaban por converger.
              rotarBusqueda();
            }
          } catch (e) {
            // un frame que falla no corta el ciclo
            console.warn('[PlateScanner] frame error', e);
          }
          if (vivoRef.current) setTimeout(ciclo, 220);
        };
        ciclo();
      } catch (e: any) {
        console.error('[PlateScanner] init error', e);
        if (!cancelado) {
          setEstado('error');
          setMensajeError(
            e?.name === 'NotAllowedError'
              ? 'No hay permiso para usar la cámara. Habilitalo en el navegador y reintentá.'
              : 'No se pudo iniciar el escáner. Verificá la conexión (los modelos se descargan la primera vez).'
          );
        }
      }
    })();

    return () => { cancelado = true; apagarTodo(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { cerrar(); } }}>
      <DialogContent className="max-w-lg overflow-hidden p-0" hideCloseButton onOpenAutoFocus={(e: any) => e.preventDefault()}>
        <div className="relative overflow-hidden bg-black" style={{ minHeight: 320 }}>
          <video
            ref={videoRef}
            playsInline
            muted
            className="block h-auto w-full"
            style={{
              maxHeight: '70vh',
              transform: zoomDigital && zoom > 1 ? `scale(${zoom})` : undefined,
              transformOrigin: 'center center'
            }}
          />

          {cuadro && (
            <div
              className={`absolute rounded border-2 ${estado === 'detectada' ? 'border-green-400' : 'border-yellow-300'}`}
              style={{
                left: `${cuadro.x * 100}%`,
                top: `${cuadro.y * 100}%`,
                width: `${cuadro.w * 100}%`,
                height: `${cuadro.h * 100}%`
              }}
            />
          )}

          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
            {/* Tocar la lectura abre el editor con ella cargada (para corregirla) */}
            <button
              type="button"
              onClick={() => { if (estado === 'buscando' && (lectura || cruda)) abrirManual(); }}
              className="rounded-full bg-black/60 px-3 py-1 text-left text-sm text-white"
            >
              {estado === 'cargando' && 'Preparando escáner...'}
              {estado === 'buscando' && (lectura ? `¿${lectura}?` : (cruda ? `Leo: ${cruda}…` : 'Apuntá a la patente'))}
              {estado === 'detectada' && `Patente: ${lectura}`}
              {estado === 'error' && 'Error'}
            </button>
            <div className="flex items-center gap-2">
              {estado !== 'detectada' && (
                <button
                  type="button"
                  onClick={abrirManual}
                  className="rounded-full bg-black/60 p-2 text-white"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              {conLinterna && (
                <button
                  type="button"
                  onClick={toggleLinterna}
                  className={`rounded-full p-2 ${linternaPrendida ? 'bg-yellow-400 text-black' : 'bg-black/60 text-white'}`}
                >
                  <BoltIcon className="h-5 w-5" />
                </button>
              )}
              <button type="button" onClick={() => { cerrar(); }} className="rounded-full bg-black/60 p-2 text-white">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Lectura tentativa y sugerencias de la base: un toque las acepta */}
          {estado === 'buscando' && (lectura || sugerencias.length > 0) && (
            <div className="absolute inset-x-0 bottom-16 flex flex-col items-center gap-2 px-3">
              {lectura && (
                <button
                  type="button"
                  onClick={confirmarManual}
                  className="rounded-xl border-2 border-green-400 bg-black/70 px-6 py-2 text-xl font-bold tracking-widest text-white active:bg-green-500"
                >
                  Usar {lectura}
                </button>
              )}
              {sugerencias.map((s) => (
                <button
                  key={s.plate}
                  type="button"
                  onClick={() => usarPatente(s.plate, 'sugerencia')}
                  className="max-w-full truncate rounded-xl border border-yellow-300 bg-black/70 px-4 py-1.5 text-sm text-white active:bg-yellow-400 active:text-black"
                >
                  ¿<span className="font-bold tracking-widest">{s.plate}</span>{s.label ? ` — ${s.label}` : ''}?
                </button>
              ))}
            </div>
          )}

          {/* Zoom para chapas lejanas */}
          {estado === 'buscando' && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-black/60 p-1">
              {nivelesZoom.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => aplicarZoom(n)}
                  className={`rounded-full px-3 py-1 text-sm font-medium ${zoom === n ? 'bg-white text-black' : 'text-white'}`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}

          {estado === 'detectada' && (
            <div className="absolute inset-x-0 bottom-0 flex justify-center p-4">
              <span className="rounded-xl bg-green-500 px-6 py-2 text-xl font-bold tracking-widest text-white">{lectura}</span>
            </div>
          )}

          {/* Carga manual: no detecta, o detecta cualquier cosa */}
          {manualAbierto && estado !== 'detectada' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 p-4">
              <div className="w-full max-w-xs space-y-2 rounded-xl bg-black/80 p-4">
                <p className="text-center text-sm text-white">
                  {manualValor ? 'Corregí lo que esté mal y tocá Usar (la corrección entrena al escáner)' : 'Escribila a mano (queda para mejorar el escáner)'}
                </p>
                <input
                  autoFocus
                  value={manualValor}
                  onChange={(e) => setManualValor(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7))}
                  placeholder="AB123CD"
                  className="w-full rounded-lg border border-white/30 bg-black/60 px-3 py-2 text-center text-2xl font-bold tracking-widest text-white outline-none focus:border-green-400"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setManualAbierto(false); manualAbiertoRef.current = false; }}
                    className="flex-1 rounded-lg bg-white/10 px-3 py-2 text-sm text-white"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={manualValor.length < 5}
                    onClick={() => usarPatente(manualValor, 'manual')}
                    className="flex-1 rounded-lg bg-green-500 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
                  >
                    Usar
                  </button>
                </div>
              </div>
            </div>
          )}

          {estado === 'error' && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <p className="text-center text-sm text-white">{mensajeError}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Boton de camara + escaner listo para poner al lado de cualquier campo patente */
export const PlateScanButton: React.FC<{ onPlate: (plate: string) => void; className?: string }> = ({ onPlate, className }) => {
  const [abierto, setAbierto] = useState(false);
  // Solo en celular o tablet: en la computadora la patente se escribe (Rodrigo,
  // 16/10/2026). Y sin cámara el botón no sirve. En los dos casos se oculta y no
  // se precargan los modelos. Cuando exista el selector de cámaras IP del
  // taller, este mismo botón será su punto de entrada.
  const [conCamara, setConCamara] = useState(() => esCelularOTablet());
  useEffect(() => {
    if (!esCelularOTablet()) return undefined;
    let vivo = true;
    let t: any = null;
    const armar = () => { t = setTimeout(precalentarEscanerPatentes, 800); };
    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then((ds) => {
          if (!vivo) return;
          const hay = ds.some((d) => d.kind === 'videoinput');
          setConCamara(hay);
          if (hay) armar();
        })
        .catch(() => { if (vivo) armar(); }); // en la duda, mostrar
    } else {
      setConCamara(false);
    }
    return () => { vivo = false; if (t) clearTimeout(t); };
  }, []);
  if (!conCamara) return null;
  return (
    <>
      <Tip label="Escanear patente con la cámara">
        <Button type="button" variant="outline" size="sm" onClick={() => setAbierto(true)} className={className}>
          <CameraIcon className="h-4 w-4" />
        </Button>
      </Tip>
      <PlateScanner open={abierto} onClose={() => setAbierto(false)} onPlate={onPlate} />
    </>
  );
};

export default PlateScanner;
